/**
 * RoyaleLobbyShell
 *
 * The SHARED page skeleton for every Puzzle Royale surface: the live
 * PuzzleRoyaleBattle screen and the scheduled-tournament registration lobby both
 * render through this, so the two are pixel-identical by construction rather
 * than by two sets of hand-copied styles that drift apart.
 *
 * Layout (identical in both callers):
 *
 *   ┌─ root (BG_PAGE) ───────────────────────────────────────────────┐
 *   │  {banner}                                                      │
 *   │  ┌─ layout (padding 20, gap 16, row when wide) ──────────────┐  │
 *   │  │ {left}      ┌─ centerCol ─────┐   ┌─ rightCol ─────────┐  │  │
 *   │  │ (optional,  │ {aboveBoard}    │   │ (own ScrollView)   │  │  │
 *   │  │  15%)       │ {board}         │   │ {rail}             │  │  │
 *   │  │             │ {belowBoard}    │   │                    │  │  │
 *   │  │             └─────────────────┘   └────────────────────┘  │  │
 *   │  └───────────────────────────────────────────────────────────┘  │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * The flex ratios are the battle screen's originals: left grid 15, center 50/60,
 * right rail 35/40 — `flanked` picks the race variant (a left column present)
 * vs the wide variant (board + rail only, which is what a lobby uses).
 *
 * `boardWidth` is computed here too (the same formula the battle screen used) so
 * both surfaces size their board identically for a given viewport.
 */

import React from 'react';
import {ScrollView, StyleSheet, View, useWindowDimensions} from 'react-native';

const BG_PAGE = '#FAF6EB';

/** Shared board sizing — must stay identical across royale surfaces.
 *  `flanked` = a left grid column is present (live race), which narrows the
 *  center column; a lobby has board + rail only. */
export function useRoyaleBoardWidth(flanked: boolean): {
  boardWidth: number;
  isWide: boolean;
} {
  const {width: viewportWidth, height: viewportHeight} = useWindowDimensions();
  const isWide = viewportWidth >= 900;
  // Above the board: app header (~135px) + page padding (~28px) + the status
  // strip (~72px) ≈ 235px; reserve that and use the rest for the board so the
  // bottom rank is never cut off.
  const heightCap = Math.max(320, viewportHeight - 235);
  let boardWidth: number;
  if (isWide) {
    const widthCap = (viewportWidth - 72) * (flanked ? 0.58 : 0.6);
    boardWidth = Math.min(900, heightCap, widthCap);
  } else {
    boardWidth = Math.min(620, heightCap, viewportWidth - 48);
  }
  return {boardWidth, isWide};
}

interface Props {
  /** Full-width slot above the layout (error banners, connection notices). */
  banner?: React.ReactNode;
  /** Left column (the live race's puzzle-grid panel). Omit for a lobby. */
  left?: React.ReactNode;
  /** Slot directly above the board (who-to-play + clock, or the lobby title card). */
  aboveBoard?: React.ReactNode;
  /** The board itself. */
  board?: React.ReactNode;
  /** Slot under the board (tip card / result card / caption). */
  belowBoard?: React.ReactNode;
  /** Right rail contents (the tab bar + active panel). */
  rail?: React.ReactNode;
  /** True when a left column is present — selects the race flex ratios. */
  flanked?: boolean;
  /** Overrides the wide/narrow decision (defaults to viewport >= 900). */
  isWide?: boolean;
}

export default function RoyaleLobbyShell({
  banner,
  left,
  aboveBoard,
  board,
  belowBoard,
  rail,
  flanked = false,
  isWide: isWideProp,
}: Props) {
  const {isWide: isWideAuto} = useRoyaleBoardWidth(flanked);
  const isWide = isWideProp ?? isWideAuto;

  return (
    <View style={styles.root}>
      {banner}
      <View
        style={[
          styles.scrollContent,
          styles.layout,
          isWide ? styles.layoutWide : styles.layoutNarrow,
        ]}>
        {left ? (
          <View style={[styles.gridCol, isWide && styles.gridColWide]}>
            {left}
          </View>
        ) : null}

        <View
          style={[
            styles.centerCol,
            isWide && (flanked ? styles.centerColRace : styles.centerColWide),
          ]}>
          {aboveBoard}
          {board}
          {belowBoard}
        </View>

        {/* The rail owns its OWN ScrollView so a long roster/leaderboard scrolls
            here — never the whole page — keeping the board out of a scroll
            parent. */}
        {rail ? (
          <ScrollView
            style={[
              styles.rightCol,
              isWide && (flanked ? styles.rightColRace : styles.rightColWide),
            ]}
            contentContainerStyle={styles.rightColContent}
            showsVerticalScrollIndicator={false}>
            {rail}
          </ScrollView>
        ) : null}
      </View>
    </View>
  );
}

/**
 * The canonical royale LAYOUT styles. Exported so PuzzleRoyaleBattle can consume
 * the very same objects instead of keeping its own copies — that is what makes
 * the live screen and the tournament lobby pixel-identical BY REFERENCE, so they
 * cannot drift apart when one is edited later.
 */
export const royaleLayoutStyles = StyleSheet.create({
  // minHeight:0 all the way down the chain. `flex:1` alone is NOT enough on
  // RN-for-Web: every flex item defaults to min-height:auto, so a tall child
  // (an expanded Details accordion) inflates each ancestor in turn and the PAGE
  // scrolls instead of the rail's own ScrollView. The chain that must all be
  // shrinkable is root → scrollContent → layoutWide → rightCol.
  root: {flex: 1, minHeight: 0, backgroundColor: BG_PAGE},
  scrollContent: {padding: 20, flex: 1, minHeight: 0},
  layout: {gap: 16},
  // 'stretch' so the right-rail ScrollView inherits the row's (screen) height
  // and can scroll internally instead of growing the page.
  layoutWide: {
    flexDirection: 'row',
    alignItems: 'stretch',
    flex: 1,
    minHeight: 0,
  },
  layoutNarrow: {flexDirection: 'column', flex: 1, minHeight: 0},
  // Raised zIndex so the grid's hover-preview board floats OVER the board column
  // (a later sibling) instead of behind it.
  gridCol: {gap: 8, zIndex: 1000},
  gridColWide: {flexGrow: 15, flexShrink: 1, flexBasis: 0, minWidth: 170},
  centerCol: {gap: 12, alignItems: 'center'},
  centerColRace: {flexGrow: 50, flexShrink: 1, flexBasis: 0, minWidth: 0},
  centerColWide: {flexGrow: 60, flexShrink: 1, flexBasis: 0, minWidth: 0},
  // minHeight:0 is what actually makes the rail's OWN ScrollView engage. A flex
  // child defaults to min-height:auto, so it refuses to shrink below its content
  // and grows the row instead — expanding "How it works" / "Scoring & streaks"
  // then scrolled the WHOLE PAGE rather than just the rail. With minHeight:0 the
  // column is bounded by the row height and the inner ScrollView takes over.
  rightCol: {gap: 12, minHeight: 0},
  rightColWide: {flexGrow: 40, flexShrink: 1, flexBasis: 0, minWidth: 260},
  rightColRace: {flexGrow: 35, flexShrink: 1, flexBasis: 0, minWidth: 220},
  rightColContent: {gap: 12, paddingBottom: 24, flexGrow: 1},
});

// Local alias so this component's JSX reads naturally.
const styles = royaleLayoutStyles;
